/**
 * CLI handlers for `nexus seed` commands.
 *
 * Export: snapshots seedable entity data from DB to data/seeds/ JSONs.
 * These JSONs are committed to the repo and imported on startup.
 */
import { existsSync, mkdirSync, writeFileSync, readdirSync, readFileSync } from 'node:fs'
import { join, basename } from 'node:path'
import { consola } from 'consola'
import { getOrderedModules } from '@kernel/engine/index.js'
import { getProjectPath } from '@kernel/config/paths.js'
import { getDb } from '@kernel/db/connection.js'
import { initCli } from './shared.js'
import { loadModulesForMigration } from './migrate-commands.js'
import type { FieldDefinition } from '@gzl10/nexus-sdk'

/** Fields that should be deserialized from JSON strings when exporting */
function deserializeJsonFields(
  record: Record<string, unknown>,
  fields: Record<string, FieldDefinition>
): Record<string, unknown> {
  const result = { ...record }
  for (const [name, field] of Object.entries(fields)) {
    if (field.db?.type === 'json' && typeof result[name] === 'string') {
      try {
        result[name] = JSON.parse(result[name] as string)
      } catch {
        // Keep as string if invalid JSON
      }
    }
  }
  return result
}

/** Fields that should be serialized to JSON strings when importing */
function serializeJsonFields(
  record: Record<string, unknown>,
  fields: Record<string, FieldDefinition>
): Record<string, unknown> {
  const result = { ...record }
  for (const [name, field] of Object.entries(fields)) {
    if (field.db?.type === 'json' && typeof result[name] === 'object' && result[name] !== null) {
      result[name] = JSON.stringify(result[name])
    }
  }
  return result
}

/**
 * Export seedable entity data from DB to data/seeds/ JSONs.
 *
 * Usage: nexus seed export [entity]
 * If entity is omitted, exports all seedable entities.
 */
export async function handleSeedExport(entity?: string): Promise<void> {
  initCli()
  await loadModulesForMigration()
  const db = getDb()

  try {
    const modules = getOrderedModules()
    const seedDir = join(getProjectPath(), 'data', 'seeds')

    // Find all seedable entity definitions
    const seedableEntities: Array<{
      module: string
      table: string
      fields: Record<string, FieldDefinition>
    }> = []

    for (const mod of modules) {
      for (const def of mod.definitions ?? []) {
        if (!('seedable' in def) || !def.seedable) continue
        if (entity && def.table !== entity) continue
        seedableEntities.push({
          module: mod.name,
          table: def.table,
          fields: def.fields,
        })
      }
    }

    if (seedableEntities.length === 0) {
      if (entity) {
        consola.warn(`No seedable entity found with table "${entity}"`)
      } else {
        consola.warn('No seedable entities found')
      }
      return
    }

    // Ensure data/seeds/ directory exists
    if (!existsSync(seedDir)) {
      mkdirSync(seedDir, { recursive: true })
    }

    for (const { module: modName, table, fields } of seedableEntities) {
      const rows = await db(table).orderBy('id')
      if (rows.length === 0) {
        consola.info(`${table}: no records, skipping`)
        continue
      }

      // Deserialize JSON fields for readability
      const exported = rows.map((row: Record<string, unknown>) =>
        deserializeJsonFields(row, fields)
      )

      const filePath = join(seedDir, `${table}.json`)
      writeFileSync(filePath, JSON.stringify(exported, null, 2) + '\n', 'utf-8')
      consola.success(`${table}: exported ${rows.length} records to data/seeds/${table}.json (module: ${modName})`)
    }
  } catch (err) {
    consola.error('Seed export failed:', err)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

/**
 * Import seed data from data/seeds/ JSONs into the database.
 * Called at startup before module seeds.
 *
 * Uses upsert (onConflict merge) to be idempotent.
 */
export async function importSeedFiles(
  db: ReturnType<typeof getDb>,
  modules: Array<{ name: string; definitions?: Array<unknown> }>,
  logger: { info: (msg: string) => void; debug: (msg: string) => void }
): Promise<void> {
  const seedDir = join(getProjectPath(), 'data', 'seeds')
  if (!existsSync(seedDir)) return

  const files = readdirSync(seedDir).filter(f => f.endsWith('.json'))
  if (files.length === 0) return

  // Build map of seedable entity definitions
  const seedableDefs = new Map<string, { fields: Record<string, FieldDefinition>; module: string }>()
  for (const mod of modules) {
    for (const rawDef of mod.definitions ?? []) {
      const def = rawDef as Record<string, unknown>
      if (!def['seedable'] || !def['table'] || !def['fields']) continue
      seedableDefs.set(
        def['table'] as string,
        { fields: def['fields'] as Record<string, FieldDefinition>, module: mod.name }
      )
    }
  }

  for (const file of files) {
    const table = basename(file, '.json')
    const defInfo = seedableDefs.get(table)
    if (!defInfo) {
      logger.debug(`data/seeds/${file}: skipped (entity "${table}" is not seedable)`)
      continue
    }

    const filePath = join(seedDir, file)
    const raw = readFileSync(filePath, 'utf-8')
    let records: Record<string, unknown>[]
    try {
      records = JSON.parse(raw)
    } catch {
      logger.info(`data/seeds/${file}: skipped (invalid JSON)`)
      continue
    }

    if (!Array.isArray(records) || records.length === 0) continue

    // Serialize JSON fields back to strings for DB insert
    const rows = records.map(r => serializeJsonFields(r, defInfo.fields))

    // Upsert each record (onConflict merge on id)
    for (const row of rows) {
      await db(table)
        .insert(row)
        .onConflict('id')
        .merge()
    }

    logger.info(`Seeded ${rows.length} records into ${table} from data/seeds/${file}`)
  }
}
